// frontend-participant/src/app.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import io from "socket.io-client";
import FieldUpdatesNotification from "./FieldUpdatesNotification";
import InterruptHandler from "./InterruptHandler";
import BenchmarkView from './components/BenchmarkView';

// Detect if we are in "form" mode (no chat input)
const isFormMode = window.location.pathname.includes("/form/");

// Simple markdown-style formatter for bot messages
const formatMessage = (text) => {
  if (typeof text !== 'string') return text;
  
  // Split by lines to handle different formatting per line
  const lines = text.split('\n');
  const formattedLines = lines.map((line, index) => {
    let formattedLine = line;
    
    // Handle bold text **text**
    formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle italic text *text* (but not when surrounded by **)
    formattedLine = formattedLine.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    
    // Handle bullet points • or - at start of line (with potential whitespace)
    if (/^\s*[•\-]\s/.test(line)) {
      const cleanLine = formattedLine.replace(/^\s*[•\-]\s*/, '');
      return (
        <div key={index} className="bullet-point">
          <span dangerouslySetInnerHTML={{ __html: cleanLine }} />
        </div>
      );
    }
    
    // Handle error messages with ❌
    if (line.includes('❌')) {
      return (
        <div key={index} className="error-message">
          <span dangerouslySetInnerHTML={{ __html: formattedLine }} />
        </div>
      );
    }
    
    // Handle progress indicators
    if (line.includes('Progress:') || line.includes('*Progress:')) {
      return (
        <div key={index} className="progress-indicator">
          <span dangerouslySetInnerHTML={{ __html: formattedLine }} />
        </div>
      );
    }
    
    // Empty lines for spacing
    if (line.trim() === '') {
      return <div key={index} className="empty-line">&nbsp;</div>;
    }
    
    // Regular line with potential formatting
    if (formattedLine !== line) {
      return (
        <div key={index}>
          <span dangerouslySetInnerHTML={{ __html: formattedLine }} />
        </div>
      );
    }
    
    return <div key={index}>{line}</div>;
  });
  
  return <div className="formatted-message">{formattedLines}</div>;
};

/* ──────────────────────────────────────────────────────────────
   Progress Bar Component
────────────────────────────────────────────────────────────── */
const ProgressBar = ({ progress }) => {
  if (!progress) return null;
  
  const { current, total, percentage, sub_progress, detailed_progress } = progress;
  
  // Calculate percentage if not provided
  let progressPercentage = percentage;
  if (!progressPercentage && current && total) {
    if (typeof current === 'string' && current.includes('.')) {
      // Handle complex progress like "8.1" - use the main number
      const mainCurrent = parseFloat(current.split('.')[0]);
      progressPercentage = Math.round((mainCurrent / total) * 100);
    } else {
      progressPercentage = Math.round((current / total) * 100);
    }
  }
  
  return (
    <div className="progress-container">
      <div className="progress-header">
        <span className="progress-text">
          Form Progress
          {sub_progress && <span className="sub-progress"> • {sub_progress}</span>}
          {detailed_progress && <span className="detailed-progress"> • {detailed_progress}</span>}
        </span>
        <span className="progress-percentage">{Math.round(progressPercentage) || 0}%</span>
      </div>
      <div className="progress-bar-track">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${progressPercentage || 0}%` }}
        />
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   Scenario side panel (audio player only)
────────────────────────────────────────────────────────────── */
const ScenarioPane = ({ text }) => {
  const [width, setWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.min(Math.max(startWidthRef.current + delta, 280), 600);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <aside className="scenario-pane" style={{ width: `${width}px` }}>
      <div className="scenario-header">🚗💥 Scenario</div>
      <div className="scenario-subheading">
        This scenario contains everything you know about the accident. Use it to look up relevant information. The chatbot does not have access to this information.
      </div>
      
      {/* Audio Player Section */}
      <div className="scenario-audio-section">
        <div className="audio-label">🎧 Listen to the scenario</div>
        <audio 
          controls 
          className="scenario-audio-player"
          preload="metadata"
          controlsList="nodownload"
        >
          <source 
            src={process.env.NODE_ENV === 'development' 
              ? "http://localhost:5000/crash_audio.mp3" 
              : "/crash_audio.mp3"
            } 
            type="audio/mpeg" 
          />
          Your browser does not support the audio element.
        </audio>
      </div>

      {/* Spacer to push task prompt to bottom */}
      <div style={{ flex: 1 }} />

      {/* Prominent Task Instruction - Stuck to bottom */}
      <div className="scenario-task-prompt">
        <div className="task-prompt-icon">⚡</div>
        <div className="task-prompt-content">
          <div className="task-prompt-title">Your Goal</div>
          <div className="task-prompt-text">
            Complete the accident report as <strong>quickly and accurately</strong> as possible. 
            Share all the details you know from the scenario in your messages—the more information you provide at once, the faster you'll be done!
          </div>
        </div>
      </div>

      <div 
        className="resize-handle" 
        onMouseDown={handleMouseDown}
        style={{ cursor: isResizing ? 'ew-resize' : 'ew-resize' }}
      />
    </aside>
  );
};

// Component for rendering clickable UI elements
const UIComponent = ({ uiData, onSelect, isCurrentQuestion = true }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [otherText, setOtherText] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);

  if (!uiData || uiData.type !== 'clickable_choice') {
    return null;
  }

  const handleOptionClick = (option) => {
    // Only allow clicks on current questions
    // Previous questions should not be clickable
    if (!isCurrentQuestion) {
      return; // Disabled - users should use "change reply" command
    }

    // Current question logic (unchanged)
    if (uiData.allow_multiple) {
      setSelectedOptions(prev => {
        if (prev.includes(option)) {
          const newOptions = prev.filter(o => o !== option);
          // Hide other input if "Other" was deselected
          if (option.toLowerCase() === 'other' && uiData.allow_other) {
            setShowOtherInput(false);
            setOtherText('');
          }
          return newOptions;
        } else {
          const newOptions = [...prev, option];
          // Show other input if "Other" was selected and other_specify is enabled
          if (option.toLowerCase() === 'other' && uiData.allow_other) {
            setShowOtherInput(true);
          }
          return newOptions;
        }
      });
    } else {
      setSelectedOptions([option]);
      if (option.toLowerCase() === 'other' && uiData.allow_other) {
        setShowOtherInput(true);
      } else {
        setShowOtherInput(false);
        // Auto-submit for single choice (except when "Other" needs specification)
        setTimeout(() => handleSubmit([option], ''), 100);
      }
    }
  };

  const handleSubmit = (options = selectedOptions, otherValue = otherText) => {
    if (options.length === 0) return;

    // For multiple choice, if "Other" is selected but no text provided, prevent submission
    if (uiData.allow_multiple && options.includes('Other') && uiData.allow_other && !otherValue.trim()) {
      return; // Don't submit - wait for other text
    }

    const response = {
      type: 'choice_selection',
      selected_options: options,
      other_text: otherValue
    };
    
    onSelect(JSON.stringify(response));
  };

  const isSelected = (option) => selectedOptions.includes(option);

  return (
    <div className={`ui-component ${!isCurrentQuestion ? 'previous-question' : ''}`}>
      {!isCurrentQuestion && (
        <div className="edit-indicator">
          💡 Previous answer - Type "change reply" to edit
        </div>
      )}
      
      <div className="ui-instructions">
        {uiData.instructions}
      </div>
      
      <div className="ui-options">
        {uiData.options.map((option, index) => (
          <button
            key={index}
            className={`ui-option-button ${isSelected(option) ? 'selected' : ''} ${!isCurrentQuestion ? 'previous-question-option' : ''} ${
              option === 'Other' && isSelected(option) && uiData.allow_other && !otherText.trim() ? 'needs-specification' : ''
            }`}
            onClick={() => handleOptionClick(option)}
            disabled={!isCurrentQuestion}
          >
            {uiData.allow_multiple && isCurrentQuestion && (
              <span className="checkbox">
                {isSelected(option) ? '☑' : '☐'}
              </span>
            )}
            {option}
            {option === 'Other' && isSelected(option) && uiData.allow_other && !otherText.trim() && (
              <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#dc3545' }}>
                (specify below)
              </span>
            )}
          </button>
        ))}
      </div>

      {showOtherInput && isCurrentQuestion && (
        <div className="other-input-container">
          <input
            type="text"
            placeholder="Please specify..."
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            className="other-input"
            autoFocus
          />
          {!otherText.trim() && (
            <div style={{
              fontSize: '0.85rem',
              color: '#856404',
              marginTop: '5px',
              fontStyle: 'italic'
            }}>
              💡 Please specify what you mean by "Other"
            </div>
          )}
        </div>
      )}

      {/* Multiple choice submit button - disabled if Other is selected but no text provided */}
      {isCurrentQuestion && uiData.allow_multiple && selectedOptions.length > 0 && (
        <div className="ui-submit-container">
          <button
    type="button"
    className="ui-submit-button"
    onClick={() => handleSubmit()}
    disabled={selectedOptions.includes('Other') && uiData.allow_other && !otherText.trim()}
    title={selectedOptions.includes('Other') && uiData.allow_other && !otherText.trim() ? 
      'Please specify what you mean by "Other" before submitting' : ''}
          >
            Submit Selection{selectedOptions.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {isCurrentQuestion && !uiData.allow_multiple && showOtherInput && otherText.trim() && (
        <div className="ui-submit-container">
          <button
            type="button"
            className="ui-submit-button"
            onClick={() => handleSubmit(['Other'], otherText)}
          >
            Submit
          </button>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// Helpers to prettify the completion payload
const coerceMaybeObject = (v) => {
  // vehicles may arrive as stringified dicts; try to parse gracefully
  if (typeof v !== "string") return v;
  try { return JSON.parse(v); } catch {}
  try { return JSON.parse(v.replace(/'/g, '"')); } catch {}
  return v;
};

const fmtYesNo = (v) => (v === true || v === "true" ? "Yes" : v === false || v === "false" ? "No" : (v ?? "—"));
const fmtTime = (v) => (typeof v === "string" && /^\d{2}:\d{2}:\d{2}$/.test(v) ? v.slice(0,5) : (v ?? "—"));
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);

// Maps question ids → nice labels
const LABELS = {
  description_of_accident: "Accident Description",
  date_of_accident: "Date",
  time_of_accident: "Time",
  location_of_accident: "Location",
  road_surface_condition: "Road Surface",
  weather: "Weather",
  lighting: "Lighting",
  number_of_vehicles_involved: "Vehicles Involved",
  apparent_contributing_factors: "Contributing Factors",
  any_injuries: "Any Injuries?",
  non_vehicle_property_damage: "Property Damage (Non-Vehicle)?",
  witnesses_present: "Witnesses Present?",
  additional_comments: "Additional Comments",
};

function key(o, k, fallback="—") {
  return o && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : fallback;
}

// ──────────────────────────────────────────────────────────────
// Schema-driven completion summary renderer
// 
// Fully driven by the questionnaire schema - no hardcoded field names
// Handles: regular fields, groups, repeat_groups, gates, branches, tables
// ──────────────────────────────────────────────────────────────
const CompletionSummary = ({ data, room, socket }) => {
  const [editedData, setEditedData] = useState(data?.summary || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const questionnaire = data?.questionnaire || {};
  const questions = questionnaire.questions || [];

  // Generic deep field updater
  const updateField = (path, value) => {
    setEditedData(prev => {
      const updated = JSON.parse(JSON.stringify(prev)); // Deep clone
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Update a field in a repeat group instance
  const updateRepeatField = (groupId, instanceIndex, fieldId, value) => {
    setEditedData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!Array.isArray(updated[groupId])) updated[groupId] = [];
      if (!updated[groupId][instanceIndex]) updated[groupId][instanceIndex] = {};
      updated[groupId][instanceIndex][fieldId] = value;
      return updated;
    });
  };

  // Update a table cell
  const updateTableCell = (tableId, rowIndex, columnId, value) => {
    setEditedData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      let tableData = updated[tableId];
      
      // Parse if stringified
      if (typeof tableData === 'string') {
        try {
          tableData = JSON.parse(tableData);
        } catch {
          tableData = [];
        }
      }
      
      if (!Array.isArray(tableData)) tableData = [];
      if (!tableData[rowIndex]) tableData[rowIndex] = {};
      tableData[rowIndex][columnId] = value;
      updated[tableId] = tableData;
      return updated;
    });
  };

  // Get value from nested path
  const getFieldValue = (path) => {
    const keys = path.split('.');
    let current = editedData;
    for (const key of keys) {
      if (current == null) return null;
      current = current[key];
    }
    return current;
  };

  // Helper: Normalize boolean values from backend
  // WORKAROUND: Backend sends "True"/"False" (Python format)
  const normalizeBool = (value) => {
    if (value === true || value === "true" || value === "True") return "true";
    if (value === false || value === "false" || value === "False") return "false";
    return "";
  };

  // Editable field component - type-aware rendering
  const EditableField = ({ value, onChange, fieldDef }) => {
    const fieldType = fieldDef?.type || "text";
    const options = fieldDef?.options;
    
    // Always call hooks at the top level (Rules of Hooks)
    const textareaRef = useRef(null);
    
    useEffect(() => {
      if (fieldType === "multiline_text" && textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }, [value, fieldType]);

    // Boolean fields
    if (fieldType === "bool") {
      return (
        <select
          className="editable-field"
          value={normalizeBool(value)}
          onChange={(e) => {
            const val = e.target.value;
            onChange(val === "true" ? true : val === "false" ? false : null);
          }}
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // Single choice with options
    if (options && fieldType === "single_choice") {
      return (
        <select
          className="editable-field"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    // Multiple choice - display as comma-separated
    if (options && fieldType === "multiple_choice") {
      const displayValue = Array.isArray(value) ? value.join(", ") : value || '';
      return (
        <input
          type="text"
          className="editable-field"
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Comma-separated values"
        />
      );
    }

    // Multiline text - auto-grow based on content
    if (fieldType === "multiline_text") {
      return (
        <textarea
          ref={textareaRef}
          className="editable-field auto-grow"
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
            // Auto-grow on change
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          rows={1}
        />
      );
    }

    // Map types to input types
    const inputType = {
      date: "date",
      time: "time",
      number: "number",
      integer: "number",
      float: "number"
    }[fieldType] || "text";

    // For text fields, auto-grow based on content length
    if (inputType === "text") {
      const stringValue = value || '';
      const minSize = 20;
      const calculatedSize = Math.max(minSize, stringValue.length + 5);
      
      return (
        <input
          type="text"
          className="editable-field"
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          size={calculatedSize}
          style={{ width: '100%' }}
        />
      );
    }

    return (
      <input
        type={inputType}
        className="editable-field"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  // Check if a value is empty (type-aware)
  const isEmpty = (value, fieldType) => {
    if (value === null || value === undefined) return true;
    if (fieldType === 'bool') return false; // false is valid for booleans
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return value === '';
  };

  // Render a regular field
  const renderRegularField = (fieldDef, parentPath = null) => {
    const fieldId = fieldDef.id;
    const path = parentPath ? `${parentPath}.${fieldId}` : fieldId;
    const value = getFieldValue(path);

    // Skip if empty
    if (isEmpty(value, fieldDef.type)) return null;

    // Table fields need special rendering
    if (fieldDef.type === "table") {
      return renderTableField(fieldDef);
    }

    const label = fieldDef.question_text || fieldId;

    return (
      <div className="summary-row" key={path}>
        <div className="summary-label">{label}</div>
        <div className="summary-value">
          <EditableField
            value={value}
            onChange={(v) => updateField(path, v)}
            fieldDef={fieldDef}
          />
        </div>
      </div>
    );
  };

  // Render a table field
  const renderTableField = (fieldDef) => {
    const tableId = fieldDef.id;
    let tableData = editedData[tableId];

    // Parse if stringified
    if (typeof tableData === 'string') {
      try {
        tableData = JSON.parse(tableData);
      } catch {
        return null; // Can't parse, skip
      }
    }

    if (!Array.isArray(tableData) || tableData.length === 0) return null;

    const columns = fieldDef.columns || [];
    const label = fieldDef.question_text || tableId;

    return (
      <div className="summary-subsection" key={tableId}>
        <div className="summary-subsection-title">{label}</div>
        <div className="table-container">
          <table className="summary-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.id}>{col.question_text || col.id}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {columns.map(col => (
                    <td key={col.id}>
                      <EditableField
                        value={row[col.id]}
                        onChange={(v) => updateTableCell(tableId, rowIdx, col.id, v)}
                        fieldDef={col}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render a group (nested object)
  const renderGroup = (groupDef) => {
    const groupId = groupDef.id;
    const groupData = editedData[groupId];

    if (!groupData || typeof groupData !== 'object' || Array.isArray(groupData)) return null;

    const fields = groupDef.fields || [];
    const renderedFields = fields
      .filter(f => f.structure_type === "regular")
      .map(f => renderRegularField(f, groupId))
      .filter(Boolean);

    if (renderedFields.length === 0) return null;

    const label = groupDef.label || groupId;

    return (
      <div className="summary-section" key={groupId}>
        <div className="summary-section-title">{label}</div>
        <div className="summary-grid two">
          {renderedFields}
        </div>
      </div>
    );
  };

  // Render a repeat group (array of instances)
  const renderRepeatGroup = (repeatDef) => {
    const groupId = repeatDef.id;
    const instances = editedData[groupId];

    if (!Array.isArray(instances) || instances.length === 0) return null;

    const fields = repeatDef.fields || [];
    const label = repeatDef.label || groupId;

    return (
      <div className="summary-section" key={groupId}>
        <div className="summary-section-title">
          {label}
          <span className="badge">{instances.length}</span>
        </div>
        <div className="vehicle-list">
          {instances.map((instance, idx) => (
            <div className="vehicle-card" key={idx}>
              <div className="vehicle-title">{label} {idx + 1}</div>
              <div className="summary-grid two">
                {fields
                  .filter(f => f.structure_type === "regular")
                  .map(fieldDef => {
                    const fieldId = fieldDef.id;
                    const value = instance[fieldId];

                    if (isEmpty(value, fieldDef.type)) return null;

                    return (
                      <div className="summary-row" key={fieldId}>
                        <div className="summary-label">{fieldDef.question_text || fieldId}</div>
                        <div className="summary-value">
                          <EditableField
                            value={value}
                            onChange={(v) => updateRepeatField(groupId, idx, fieldId, v)}
                            fieldDef={fieldDef}
                          />
                        </div>
                      </div>
                    );
                  })
                  .filter(Boolean)
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render a gate (conditional fields)
  const renderGate = (gateDef) => {
    const gateConfig = gateDef.gate || {};
    const gateOnField = gateConfig.gate_on;
    const whenValues = gateConfig.when_values || [];

    // Check if gate condition is met
    const gateValue = editedData[gateOnField];
    
    // Normalize boolean gate values for comparison
    let normalizedGateValue = gateValue;
    if (gateValue === "True") normalizedGateValue = true;
    if (gateValue === "False") normalizedGateValue = false;
    
    const isActive = whenValues.includes(normalizedGateValue);
    if (!isActive) return null;

    const fields = gateDef.fields || [];
    const renderedContent = fields
      .map(fieldDef => {
        if (fieldDef.structure_type === "regular") {
          if (fieldDef.type === "table") {
            return renderTableField(fieldDef);
          }
          return renderRegularField(fieldDef);
        }
        return null;
      })
      .filter(Boolean);

    if (renderedContent.length === 0) return null;

    return (
      <div className="summary-subsection" key={gateDef.id}>
        {renderedContent}
      </div>
    );
  };

  // Render a branch node
  const renderBranch = (branchDef) => {
    const branchConfig = branchDef.branch || {};
    const branchOnField = branchConfig.branch_on;
    const routes = branchConfig.routes || [];
    const defaultChildren = branchConfig.default_children || [];

    const branchValue = editedData[branchOnField];

    // Find matching route
    const matchedRoute = routes.find(r => r.when_value === branchValue);
    const childrenToRender = matchedRoute ? matchedRoute.children : defaultChildren;

    const renderedChildren = childrenToRender
      .map((child, idx) => renderSchemaNode(child, idx))
      .filter(Boolean);

    return renderedChildren.length > 0 ? (
      <div key={branchDef.id}>{renderedChildren}</div>
    ) : null;
  };

  // Walk schema and render node
  const renderSchemaNode = (node, key = null) => {
    const structureType = node.structure_type;

    switch (structureType) {
      case "regular":
        return renderRegularField(node);
      case "group":
        return renderGroup(node);
      case "repeat_group":
        return renderRepeatGroup(node);
      case "gate":
        return renderGate(node);
      case "branch":
        return renderBranch(node);
      default:
        return null;
    }
  };

  // Render all schema nodes in order, grouping regular fields into sections
  const renderAllContent = () => {
    const sections = [];
    let currentRegularFields = [];

    const flushRegularFields = () => {
      if (currentRegularFields.length > 0) {
        sections.push(
          <div className="summary-section" key={`regular-${sections.length}`}>
            <div className="summary-section-title">Form Details</div>
            <div className="summary-grid two">
              {currentRegularFields}
            </div>
          </div>
        );
        currentRegularFields = [];
      }
    };

    questions.forEach((node, idx) => {
      const rendered = renderSchemaNode(node, idx);
      
      if (!rendered) return;

      // Structure nodes (group, repeat, gate, branch) get their own section
      if (node.structure_type !== "regular") {
        flushRegularFields();
        sections.push(rendered);
      } else {
        // Regular fields accumulate
        currentRegularFields.push(rendered);
      }
    });

    flushRegularFields();

    return sections.length > 0 ? sections : (
      <div className="summary-section">
        <p className="muted">No form data to display.</p>
      </div>
    );
  };

  return (
    <div className="summary-card">
      <div className="summary-header">
        <div className="summary-icon">🎉</div>
        <div>
          <div className="summary-title">{data.title || "Form Completed Successfully!"}</div>
          <div className="summary-sub">{data.message || "Please review and edit if needed."}</div>
        </div>
      </div>

      {renderAllContent()}

      {submitSuccess && (
        <div style={{
          padding: '20px 25px',
          margin: '20px 0',
          backgroundColor: '#d4edda',
          border: '2px solid #28a745',
          borderRadius: '12px',
          color: '#155724',
          fontSize: '1.1rem',
          fontWeight: '600',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>✅</div>
          <div style={{ marginBottom: '15px' }}>Form submitted successfully!</div>
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#fff',
            border: '2px dashed #28a745',
            borderRadius: '8px',
            fontSize: '1.05rem',
            fontWeight: '700',
            color: '#28a745'
          }}>
            👉 Click the arrow in the bottom right to continue with the survey
          </div>
        </div>
      )}

      {submitError && (
        <div style={{
          padding: '15px',
          margin: '20px 0',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          color: '#721c24'
        }}>
          ❌ Error submitting form: {submitError}
        </div>
      )}

      <div className="summary-submit-container">
        <button 
          className="summary-submit-button"
          onClick={() => {
            if (!socket || !room) {
              console.error("Socket or room not available");
              setSubmitError("Connection error - please refresh the page");
              return;
            }

            setIsSubmitting(true);
            setSubmitError(null);

            console.log("Submitting edited form data:", editedData);

            // Send the edited data to the backend
            socket.emit("submit_user_edited_form", {
              room: room,
              form_data: editedData,
              questionnaire: questionnaire
            });

            // Listen for success/error responses
            const handleSuccess = (response) => {
              console.log("Form submission successful:", response);
              setIsSubmitting(false);
              setSubmitSuccess(true);
              socket.off("form_submission_success", handleSuccess);
              socket.off("form_submission_error", handleError);
              
              // Notify parent frame (e.g., Qualtrics) that form is submitted
              if (window.parent !== window) {
                window.parent.postMessage({
                  type: 'formSubmitted',
                  status: 'success',
                  room: room,
                  timestamp: new Date().toISOString()
                }, 'https://migroup.qualtrics.com');
                console.log("✅ Notified parent frame of successful submission");
              }
            };

            const handleError = (response) => {
              console.error("Form submission error:", response);
              setIsSubmitting(false);
              setSubmitError(response.error || "Unknown error occurred");
              socket.off("form_submission_success", handleSuccess);
              socket.off("form_submission_error", handleError);
            };

            socket.on("form_submission_success", handleSuccess);
            socket.on("form_submission_error", handleError);
          }}
          disabled={isSubmitting || submitSuccess}
        >
          {isSubmitting ? "Submitting..." : submitSuccess ? "✓ Submitted" : "Submit Form"}
        </button>
      </div>
    </div>
  );
};

// Component for rendering complete bot messages with UI
const BotMessage = ({ message, onUISelect, isLatestBotMessage = false, fieldUpdates = null, room = null, socket = null }) => {
  // Try to parse message text as UI message
  let uiMessage = null;
  let displayText = message.text;
  
  try {
    if (typeof message.text === 'string' && message.text.trim().startsWith('{')) {
      const parsed = JSON.parse(message.text);
      if (parsed.sender === 'bot' && parsed.ui_component) {
        uiMessage = parsed;
        displayText = parsed.text;
      }
    }
  } catch (e) {
    // Not a UI message, use original text
  }

  const isCurrentQuestion = uiMessage && uiMessage.ui_component && uiMessage.ui_component.is_current_question && isLatestBotMessage;

  const isCompletion =
    uiMessage && uiMessage.ui_component && uiMessage.ui_component.type === "form_completion";

  // Check if this message has progress information
  const hasProgress = uiMessage && uiMessage.ui_component && uiMessage.ui_component.progress;

  // Helper to get field label from question ID
  const getFieldLabel = (fieldId) => {
    // Try to find the question in the UI message's questionnaire/form data
    // For now, use a more robust fallback that shows the actual field ID
    
    // First, humanize the field ID for better readability
    const humanized = fieldId
      .replace(/_/g, ' ')
      .replace(/\./g, ' › ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return humanized;
  };

  return (
    <div className="bot-message-wrapper">
      {/* Field updates notification (shown above the message) */}
      {fieldUpdates && fieldUpdates.length > 0 && (
        <FieldUpdatesNotification 
          updates={fieldUpdates} 
          getFieldLabel={getFieldLabel}
        />
      )}

      <div className="message-content">
        {/* Show progress bar for current questions with progress info */}
        {hasProgress && isCurrentQuestion && (
          <ProgressBar progress={uiMessage.ui_component.progress} />
        )}
        
        {isCompletion
          ? (
            <div className="completion-header">
              <span className="completion-emoji">🎉</span>
              <div>
                <div className="completion-title">
                  {formatMessage(displayText)}
                </div>
              </div>
            </div>
          )
          : formatMessage(displayText)
        }
      </div>

      {isCompletion ? (
        <CompletionSummary data={uiMessage.ui_component} room={room} socket={socket} />
      ) : (
        uiMessage && uiMessage.ui_component && (
          <UIComponent
            uiData={uiMessage.ui_component}
            onSelect={onUISelect}
            isCurrentQuestion={isCurrentQuestion}
          />
        )
      )}
    </div>
  );
};

// Environment-aware socket connection
const socket = io(
  process.env.NODE_ENV === 'development' 
    ? "http://localhost:5000" 
    : window.location.origin,
  {
    transports: ["websocket"],
    path: "/socket.io"
  }
);                       

/* little marketing‑style status lines the user sees while waiting */
const WAITING_HINTS = [
  "Thinking…",
  "Consulting the form wizard…",
  "Matching your reply to the form…",
  "Retrieving relevant information…",
  "Generating a response",
  "Just one moment…",
  "Almost there…",
  "Your form wizard is working hard…",
];

export default function App() {
  /* ─────────────────────────────── state */
  const [room] = useState(
    window.location.pathname.split("/").pop()
  );                                         // /chat/<room>
  const [messages, setMessages] = useState([]);
  const [current, setCurrent] = useState("");
  const [wizardTyping, setWizardTyping] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [waitingHint, setWaitingHint] = useState(WAITING_HINTS[0]);
  const [fieldUpdatesMap, setFieldUpdatesMap] = useState(new Map()); // Map message ID -> field updates
  const [fontSize, setFontSize] = useState('normal'); // Font size state: 'normal', 'large', 'xlarge'
  const [botStatus, setBotStatus] = useState({ 
    active: false, 
    available: false,
    bot_type: null,
    debug_mode: false
  });
  
  // Check if we're in benchmark mode
  const isBenchmarkMode = botStatus.bot_type === 'benchmark';
  
  // Debug logging
  console.log('🔍 [Participant] Room:', room);
  console.log('🔍 [Participant] botStatus:', botStatus);
  console.log('🔍 [Participant] bot_type:', botStatus.bot_type);
  console.log('🔍 [Participant] isBenchmarkMode:', isBenchmarkMode);
  console.log('🔍 [Participant] Will render:', isBenchmarkMode ? 'BenchmarkView' : 'Chat');

  /* NEW: scenario text source (window → ?scenario= → localStorage) */
  const [scenarioText] = useState(() => {
    if (typeof window === "undefined") return "";
    if (window.__SCENARIO__) return String(window.__SCENARIO__);
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = qs.get("scenario");
    if (fromQuery) return fromQuery;
    const fromStorage = localStorage.getItem("scenarioText");
    return fromStorage || "";
  });

  /* refs */
  const bottomRef        = useRef(null);
  const listRef          = useRef(null);
  const autoScrollRef    = useRef(true);     // track if user is at bottom
  const waitingTimerRef  = useRef(null);
  const inputRef         = useRef(null);

  /* ─────────────────────────────── fetch bot status on mount */
  useEffect(() => {
    // Fetch initial bot status to detect benchmark mode
    const API_ORIGIN = process.env.NODE_ENV === 'development' 
      ? "http://localhost:5000" 
      : "";
    
    console.log('Fetching initial bot status for room:', room);
    fetch(`${API_ORIGIN}/api/bot/${room}/status`)
      .then(r => r.json())
      .then(status => {
        console.log('Received bot status from API:', status);
        const newStatus = {
          active: status.active || false,
          available: status.available || false,
          bot_type: status.bot_type || null,
          debug_mode: status.debug_mode || false
        };
        console.log('Setting botStatus to:', newStatus);
        setBotStatus(newStatus);
      })
      .catch(err => console.error('Failed to fetch bot status:', err));
  }, [room]);

  /* ─────────────────────────────── socket life‑cycle */
  useEffect(() => {
    socket.emit("join", { room, type: "participant", client_mode: isFormMode ? "plain_form" : undefined });

    socket.on("message_history", ({ messages }) => {
      // Prettify participant choice_selection messages and filter out interrupt messages
      const prettifyParticipantMessage = (msg) => {
        if (msg.sender !== "participant" || typeof msg.text !== "string") return msg;
        try {
          const parsed = JSON.parse(msg.text);
          if (parsed.type === 'choice_selection') {
            let displayText = msg.text;
            if (parsed.selected_options.length === 1 && !parsed.other_text) {
              displayText = parsed.selected_options[0];
            } else if (parsed.selected_options.includes('Other') && parsed.other_text) {
              displayText = `Other: ${parsed.other_text}`;
            } else if (parsed.selected_options.length > 1) {
              displayText = parsed.selected_options.join(', ');
            }
            return { ...msg, text: displayText };
          }
        } catch (e) {}
        return msg;
      };
      
      // Filter out interrupt messages (they're handled by InterruptHandler now)
      const filterInterruptMessages = (msg) => {
        if (msg.sender === "bot" && typeof msg.text === "string") {
          try {
            const parsed = JSON.parse(msg.text);
            if (parsed.type === "interrupt") {
              return false; // Don't show interrupt messages in chat
            }
          } catch (e) {}
        }
        return true;
      };
      
      setMessages((messages || [])
        .filter(filterInterruptMessages)
        .map(prettifyParticipantMessage));
    });

    socket.on("new_message", (msg) => {
      // Filter out interrupt messages - they're handled by InterruptHandler
      if (msg.sender === "bot" && typeof msg.text === "string") {
        try {
          const parsed = JSON.parse(msg.text);
          if (parsed.type === "interrupt") {
            return; // Don't add interrupt messages to chat
          }
        } catch (e) {}
      }
      
      if (msg.sender === "wizard") {
        // replace any existing streaming message
        setMessages((prev) => [
          ...prev.filter((m) => m.sender !== "wizard_streaming"),
          msg,
        ]);
        setWizardTyping(false);
        stopWaitingAnimation();
        // Refocus input after wizard response
        setTimeout(() => inputRef.current?.focus(), 100);
      } else if (msg.sender === "bot") {
        // Handle bot messages - add them directly to the message list
        setMessages((prev) => [
          ...prev.filter((m) => m.sender !== "wizard_streaming"),
          msg,
        ]);
        setWizardTyping(false);
        stopWaitingAnimation();
        // Refocus input after bot response
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    });

    // Listen for field updates from flexAI_new bot
    socket.on("field_updates", (data) => {
      const { updates } = data;
      if (updates && updates.length > 0) {
        // When new updates arrive, convert ALL previous "active" sources to "passive"
        // This ensures only the most recent update shows as "Current"
        setFieldUpdatesMap(prevMap => {
          const newMap = new Map();
          
          // Convert all existing updates to have "passive" source
          for (const [key, existingUpdates] of prevMap.entries()) {
            const convertedUpdates = existingUpdates.map(update => ({
              ...update,
              source: "passive"  // All previous updates become "Background"
            }));
            newMap.set(key, convertedUpdates);
          }
          
          return newMap;
        });
        
        // Associate new updates with the last bot message (these will have their original sources)
        setMessages((prev) => {
          // Find the last bot message
          const lastBotIndex = [...prev].reverse().findIndex(m => m.sender === "bot");
          if (lastBotIndex === -1) return prev;
          
          const actualIndex = prev.length - 1 - lastBotIndex;
          const lastBotMessage = prev[actualIndex];
          
          // Store updates in map keyed by message ID (or timestamp + sender)
          const msgKey = lastBotMessage.id || `${lastBotMessage.timestamp}-${lastBotMessage.sender}`;
          setFieldUpdatesMap(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(msgKey, updates);
            return newMap;
          });
          
          return prev;
        });
      }
    });

    socket.on("stream_chunk", ({ word, is_last }) => {
      setWizardTyping(true);
      stopWaitingAnimation();                // hide “AI is processing…”
      setMessages((prev) => {
        const last = prev.at(-1);
        if (last?.sender === "wizard_streaming") {
          last.text += ` ${word}`;
          return [...prev.slice(0, -1), last];
        }
        return [...prev, { sender: "wizard_streaming", text: word }];
      });
      if (is_last) {
        setWizardTyping(false);
        // Refocus input after streaming completes
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    });

    // Listen for bot status changes (including interrupts)
    socket.on("bot_status_changed", (data) => {
      console.log('Participant received bot_status_changed:', data);
      if (data.room === room) {
        console.log('Updating botStatus to:', {
          active: data.active || false,
          available: data.available || false,
          bot_type: data.bot_type || null,
          debug_mode: data.debug_mode || false
        });
        setBotStatus({
          active: data.active || false,
          available: data.available || false,
          bot_type: data.bot_type || null,
          debug_mode: data.debug_mode || false
        });
      }
    });

    return () => socket.emit("leave", { room });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  /* smart autoscroll: scroll to bottom for new messages */
  useEffect(() => {
    // Always scroll to bottom when new messages arrive or when waiting/typing states change
    const shouldScroll = autoScrollRef.current || waiting || wizardTyping;
    if (shouldScroll) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [messages, wizardTyping, waiting]);

  /* detect manual scrolls and update auto-scroll preference */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    
    const handleScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
      autoScrollRef.current = nearBottom;
    };
    
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  /* Initial focus on input when component mounts */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /* ─────────────────────────────── participant actions */
  const send = (e) => {
    e.preventDefault();
    if (!current.trim() || waiting) return;

    setMessages((p) => [...p, { sender: "participant", text: current }]);
    socket.emit("participant_message", { room, text: current });
    socket.emit("participant_typing", { room, text: "" });
    setCurrent("");
    startWaitingAnimation();
    
    // Ensure we scroll to bottom after sending
    autoScrollRef.current = true;
  };

  const handleUISelection = (selectionData) => {
    // Display the selection as a participant message
    let displayText = selectionData;
    try {
      const parsed = JSON.parse(selectionData);
      if (parsed.type === 'choice_selection') {
        if (parsed.selected_options.length === 1 && !parsed.other_text) {
          displayText = parsed.selected_options[0];
        } else if (parsed.selected_options.includes('Other') && parsed.other_text) {
          displayText = `Other: ${parsed.other_text}`;
        } else if (parsed.selected_options.length > 1) {
          displayText = parsed.selected_options.join(', ');
        }
      }
    } catch (e) {
      // Use original data as fallback
    }

    setMessages((p) => [...p, { sender: "participant", text: displayText }]);
    socket.emit("participant_message", { room, text: selectionData });
    startWaitingAnimation();
    
    // Ensure we scroll to bottom after sending
    autoScrollRef.current = true;
  };

  /* ─────────────────────────────── waiting animation helpers */
  const startWaitingAnimation = () => {
    setWaiting(true);
    setWaitingHint(WAITING_HINTS[0]);
    let i = 1;
    waitingTimerRef.current = setInterval(() => {
      setWaitingHint(WAITING_HINTS[i % WAITING_HINTS.length]);
      i += 1;
    }, 2700); // change hint every 3 seconds
  };

  const stopWaitingAnimation = () => {
    if (waitingTimerRef.current) clearInterval(waitingTimerRef.current);
    waitingTimerRef.current = null;
    setWaiting(false);
    // Ensure we scroll to bottom when responses arrive
    autoScrollRef.current = true;
  };

  /* ─────────────────────────────── font size toggle */
  const increaseFontSize = () => {
    setFontSize(prev => {
      if (prev === 'normal') return 'large';
      if (prev === 'large') return 'xlarge';
      return 'xlarge'; // already at max
    });
  };

  const decreaseFontSize = () => {
    setFontSize(prev => {
      if (prev === 'xlarge') return 'large';
      if (prev === 'large') return 'normal';
      return 'normal'; // already at min
    });
  };

/* ─────────────────────────────── UI */

// If benchmark mode, show BenchmarkView instead of chat
if (isBenchmarkMode) {
  return (
    <div className={`App font-size-${fontSize}`}>
      <BenchmarkView socket={socket} room={room} />
    </div>
  );
}

return (
  <div className={`App font-size-${fontSize}`}>
    <div className="font-size-controls">
      <div className="font-size-buttons">
        <button 
          className="font-size-btn" 
          onClick={decreaseFontSize}
          disabled={fontSize === 'normal'}
          aria-label="Decrease text size"
          title="Decrease text size"
        >
          −
        </button>
        <button 
          className="font-size-btn" 
          onClick={increaseFontSize}
          disabled={fontSize === 'xlarge'}
          aria-label="Increase text size"
          title="Increase text size"
        >
          +
        </button>
      </div>
      <span className="font-size-label">Change Text Size</span>
    </div>
    <div className="page-shell">
      <div className="two-col">
        {/* LEFT: scenario */}
        <ScenarioPane text={scenarioText} />

        {/* RIGHT: chat */}
        <div className="main-col">
          {/* Interrupt handler for debug mode - positioned at main-col level */}
          <InterruptHandler room={room} socket={socket} botStatus={botStatus} />
          
          <div className="chat-header">
            <h2>🧙‍♂️ FormGenius</h2>
            <p className="chat-header-instruction">
              <strong>💡 How to use this efficiently:</strong><br/>
              Instead of answering questions one by one, <strong>share all relevant information in one message</strong>. 
              I can fill out multiple form fields at once based on what you tell me!
            </p>

          </div>

          <div className="chat-window">
            <div ref={listRef} className="messages">
              {messages.map((m, i) => {
                const isLatestBotMessage =
                  m.sender === "bot" &&
                  i === messages.findLastIndex((msg) => msg.sender === "bot");

                let isCompletionMessage = false;
                if (m.sender === "bot") {
                  try {
                    if (
                      typeof m.text === "string" &&
                      m.text.trim().startsWith("{")
                    ) {
                      const parsed = JSON.parse(m.text);
                      if (
                        parsed.sender === "bot" &&
                        parsed.ui_component &&
                        parsed.ui_component.type === "form_completion"
                      ) {
                        isCompletionMessage = true;
                      }
                    }
                  } catch {}
                }

                // Get field updates for this message (if any)
                const msgKey = m.id || `${m.timestamp}-${m.sender}`;
                const fieldUpdates = fieldUpdatesMap.get(msgKey);

                return (
                  <div
                    key={i}
                    className={`message ${m.sender}${
                      isCompletionMessage ? " completion-message" : ""
                    }`}
                  >
                    {(m.sender === "wizard" ||
                      m.sender === "wizard_streaming" ||
                      m.sender === "bot") && (
                      <span className="wizard-emoji">
                        {m.sender === "bot" ? "✨" : "🧙‍♂️"}
                      </span>
                    )}

                    {m.sender === "bot" ? (
                      <BotMessage
                        message={m}
                        onUISelect={handleUISelection}
                        isLatestBotMessage={isLatestBotMessage}
                        fieldUpdates={fieldUpdates}
                        room={room}
                        socket={socket}
                      />
                    ) : (
                      <div className="message-content">
                        {typeof m.text === "object" ? (
                          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                            {JSON.stringify(m.text, null, 2)}
                          </pre>
                        ) : m.sender === "wizard" ||
                          m.sender === "wizard_streaming" ? (
                          formatMessage(m.text)
                        ) : (
                          m.text
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {waiting && !wizardTyping && (
                <div className="waiting-indicator">
                  <div className="spinner" />
                  {waitingHint}
                </div>
              )}

              {wizardTyping && (
                <div className="message wizard typing-indicator">
                  <span className="wizard-emoji">🧙‍♂️</span>
                  <div className="typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {!isFormMode && (
              <form className="message-form" onSubmit={send}>
                <input
                  ref={inputRef}
                  value={current}
                  onChange={(e) => {
                    setCurrent(e.target.value);
                    socket.emit("participant_typing", {
                      room,
                      text: e.target.value,
                    });
                  }}
                  onPaste={(event) => event.preventDefault()}
                  placeholder="Type your message…"
                />
                <button disabled={waiting || !current.trim()}>Send</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

}
